import { lazy, Suspense } from 'react'
import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useParams,
} from 'react-router-dom'
import { AnalyticsRouteTracker } from './components/AnalyticsRouteTracker'
import { Footer } from './components/Footer'
import { Header } from './components/Header'
import { TopBar } from './components/TopBar'
import { getPreferredLocale, LocaleProvider } from './i18n/LocaleContext'
import { isLocale, localePath, pathWithoutLocale } from './i18n/routes'
import { HomePage } from './pages/HomePage'
import './App.css'

const CatalogPage = lazy(() =>
  import('./pages/CatalogPage').then((m) => ({ default: m.CatalogPage }))
)
const FilesCatalogPage = lazy(() =>
  import('./pages/FilesCatalogPage').then((m) => ({
    default: m.FilesCatalogPage,
  }))
)
const ProductPage = lazy(() =>
  import('./pages/ProductPage').then((m) => ({ default: m.ProductPage }))
)
const FilePage = lazy(() =>
  import('./pages/FilePage').then((m) => ({ default: m.FilePage }))
)
const LinksPage = lazy(() =>
  import('./pages/LinksPage').then((m) => ({ default: m.LinksPage }))
)
const OriginalArtworksCatalogPage = lazy(() =>
  import('./pages/OriginalArtworksCatalogPage').then((m) => ({
    default: m.OriginalArtworksCatalogPage,
  }))
)
const PrintsCatalogPage = lazy(() =>
  import('./pages/PrintsCatalogPage').then((m) => ({
    default: m.PrintsCatalogPage,
  }))
)
const OriginalArtworkPage = lazy(() =>
  import('./pages/OriginalArtworkPage').then((m) => ({
    default: m.OriginalArtworkPage,
  }))
)
const PrintPage = lazy(() =>
  import('./pages/PrintPage').then((m) => ({ default: m.PrintPage }))
)
const HelloPage = lazy(() =>
  import('./pages/HelloPage').then((m) => ({ default: m.HelloPage }))
)

function PageFallback() {
  return (
    <div className="content-wrap">
      <p className="status" aria-live="polite">
        …
      </p>
    </div>
  )
}

function SiteLayout() {
  return (
    <div className="app-shell">
      <TopBar />
      <Header />
      <main className="site-main">
        <Suspense fallback={<PageFallback />}>
          <Outlet />
        </Suspense>
      </main>
      <Footer />
    </div>
  )
}

function LocaleGuard() {
  const { locale } = useParams()
  if (!isLocale(locale)) {
    return <Navigate to={`/${getPreferredLocale()}`} replace />
  }
  return <Outlet />
}

function RootRedirect() {
  return <Navigate to={`/${getPreferredLocale()}`} replace />
}

function LegacyRedirect() {
  const location = useLocation()
  const preferred = getPreferredLocale()
  const target =
    localePath(preferred, pathWithoutLocale(location.pathname)) +
    location.search
  return <Navigate to={target} replace />
}

function RedirectToMiniature() {
  const { slug } = useParams()
  const preferred = getPreferredLocale()
  return (
    <Navigate to={localePath(preferred, `/miniatures/${slug}`)} replace />
  )
}

function RedirectLegacyArtwork() {
  const { slug } = useParams()
  const preferred = getPreferredLocale()
  const path = slug
    ? `/hand-drawn-originals/${slug}`
    : '/hand-drawn-originals'
  return <Navigate to={localePath(preferred, path)} replace />
}

function RedirectArtworkPath() {
  const { locale, slug } = useParams()
  const path = slug
    ? `/hand-drawn-originals/${slug}`
    : '/hand-drawn-originals'
  return <Navigate to={localePath(locale, path)} replace />
}

function App() {
  return (
    <BrowserRouter>
      <LocaleProvider>
        <AnalyticsRouteTracker />
        <Routes>
          <Route path="/" element={<RootRedirect />} />

          <Route
            path="/catalog"
            element={
              <Navigate
                to={localePath(getPreferredLocale(), '/miniatures')}
                replace
              />
            }
          />
          <Route path="/products/:slug" element={<RedirectToMiniature />} />
          <Route path="/miniatures" element={<LegacyRedirect />} />
          <Route path="/miniatures/:slug" element={<LegacyRedirect />} />
          <Route path="/files" element={<LegacyRedirect />} />
          <Route path="/files/:slug" element={<LegacyRedirect />} />
          <Route path="/links" element={<LegacyRedirect />} />
          <Route path="/original-artworks" element={<RedirectLegacyArtwork />} />
          <Route path="/original-artworks/:slug" element={<RedirectLegacyArtwork />} />
          <Route path="/hand-drawn-originals" element={<LegacyRedirect />} />
          <Route path="/hand-drawn-originals/:slug" element={<LegacyRedirect />} />
          <Route path="/prints" element={<LegacyRedirect />} />
          <Route path="/prints/:slug" element={<LegacyRedirect />} />

          <Route path="/hello" element={<SiteLayout />}>
            <Route index element={<HelloPage />} />
            <Route path=":slug" element={<HelloPage />} />
          </Route>

          <Route path="/:locale" element={<LocaleGuard />}>
            <Route element={<SiteLayout />}>
              <Route index element={<HomePage />} />
              <Route path="original-artworks" element={<RedirectArtworkPath />} />
              <Route path="original-artworks/:slug" element={<RedirectArtworkPath />} />
              <Route path="hand-drawn-originals" element={<OriginalArtworksCatalogPage />} />
              <Route path="hand-drawn-originals/:slug" element={<OriginalArtworkPage />} />
              <Route path="prints" element={<PrintsCatalogPage />} />
              <Route path="prints/:slug" element={<PrintPage />} />
              <Route path="miniatures" element={<CatalogPage />} />
              <Route path="miniatures/:slug" element={<ProductPage />} />
              <Route path="files" element={<FilesCatalogPage />} />
              <Route path="files/:slug" element={<FilePage />} />
              <Route path="links" element={<LinksPage />} />
              <Route path="hello" element={<HelloPage />} />
              <Route path="hello/:slug" element={<HelloPage />} />
            </Route>
          </Route>
        </Routes>
      </LocaleProvider>
    </BrowserRouter>
  )
}

export default App
